import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import EnrollmentCTA, { ReenrollmentCTA } from "../components/EnrollmentCTA";
import { useSettings } from "../contexts/SettingsContext";
import { ArrowRight, BookOpen, GraduationCap, Heart, Sparkles, Users, ShieldCheck } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1761862062991-13fd86da2581?fm=jpg&q=80&w=2400&auto=format&fit=crop";

const matieres = [
  { key: "arabe", title: "Arabe", description: "Apprentissage de l'arabe pour lire le Coran.", icon: BookOpen },
  { key: "coran", title: "Coran", description: "Mémorisation et compréhension du Livre Saint et Tajwîd, au rythme des élèves.", icon: Sparkles },
  { key: "education_musulmane", title: "Éducation Musulmane", description: "Compréhension des principes fondamentaux de notre belle religion, comportement, sîra et hadiths.", icon: Heart },
];

const steps = [
  { i: 1, title: "Remplir la demande", text: "Formulaire en ligne pour le représentant légal et l'enfant.", icon: BookOpen },
  { i: 2, title: "Validation par l'admin", text: "Notre équipe étudie votre demande et vous répartit dans le bon niveau.", icon: ShieldCheck },
  { i: 3, title: "Paiement en présentiel", text: "Une permanence est tenue à une date communiquée pour finaliser le paiement.", icon: Users },
];

export default function LandingPage() {
  const { feeTiers } = useSettings();
  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Architecture" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FCFBF8]/95 via-[#FCFBF8]/80 to-[#1E3A8A]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-36 lg:py-44">
          <div className="max-w-2xl fade-in">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C86444]" data-testid="hero-overline">
              Mosquée des Alpes · Teisseire · Grenoble
            </span>
            <h1 className="mt-5 font-[Outfit] font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1A2547]">
              Apprendre la <em className="not-italic text-[#1E3A8A]">religion</em>, transmettre la <em className="not-italic text-[#1E3A8A]">tradition</em>.
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#5C6861] leading-relaxed max-w-xl">
              Cours d'arabe, de Coran et d'éducation musulmane répartis dans plusieurs <strong>groupes pédagogiques</strong>.
              Inscription unique pour les 3 matières — pour enfants et adolescents.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <EnrollmentCTA size="lg" testId="cta-request" />
              <ReenrollmentCTA size="lg" testId="cta-reenrollment" />
            </div>
          </div>
        </div>
      </section>

      {/* Matières */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C86444]">Nos enseignements</span>
          <h2 className="mt-3 font-[Outfit] font-semibold text-3xl md:text-4xl tracking-tight text-[#1A2547]">
            Trois matières, plusieurs groupes.
          </h2>
          <p className="mt-4 text-[#5C6861] leading-relaxed">
            Chaque inscription couvre les 3 matières au sein d'un même groupe pédagogique. La répartition est définie par l'équipe selon l'âge et le profil de l'enfant.
          </p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 stagger" data-testid="matieres-grid">
          {matieres.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.key} className="bg-white border border-[#EAE6DF] rounded-2xl p-7 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(61,90,71,0.08)] transition-all duration-300" data-testid={`matiere-${c.key}`}>
                <div className="w-11 h-11 rounded-xl bg-[#E0E7FF] flex items-center justify-center text-[#1E3A8A]">
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <h3 className="mt-5 font-[Outfit] font-semibold text-xl text-[#1A2547]">{c.title}</h3>
                <p className="mt-2 text-sm text-[#5C6861] leading-relaxed">{c.description}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-10 flex justify-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-3 bg-[#E0E7FF] border border-[#1E3A8A]/20 rounded-2xl px-5 py-3 max-w-2xl">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C86444]">Cotisation annuelle (par famille)</span>
            <div className="flex flex-wrap gap-2">
              <FeeTile label="1 enfant" amount={feeTiers.one_child} />
              <FeeTile label="2 enfants" amount={feeTiers.two_children} />
              <FeeTile label="3+ enfants" amount={feeTiers.three_plus_children} />
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#F4F1EB]/60 border-y border-[#EAE6DF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C86444]">Comment s'inscrire</span>
            <h2 className="mt-3 font-[Outfit] font-semibold text-3xl md:text-4xl tracking-tight text-[#1A2547]">Un processus simple en 3 étapes.</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 stagger">
            {steps.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.i} className="bg-white border border-[#EAE6DF] rounded-2xl p-7" data-testid={`step-${s.i}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center font-[Outfit] font-semibold">{s.i}</div>
                    <Icon className="w-5 h-5 text-[#1E3A8A]" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-5 font-[Outfit] font-semibold text-xl text-[#1A2547]">{s.title}</h3>
                  <p className="mt-2 text-sm text-[#5C6861] leading-relaxed">{s.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-20 md:pb-28">
        <div className="bg-[#1E3A8A] text-white rounded-3xl p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#C86444] opacity-10" />
          <div className="absolute -bottom-32 -left-20 w-72 h-72 rounded-full bg-[#E8A365] opacity-10" />
          <div className="relative max-w-2xl">
            <GraduationCap className="w-10 h-10 text-[#E8A365]" strokeWidth={1.5} />
            <h2 className="mt-5 font-[Outfit] font-semibold text-3xl md:text-4xl tracking-tight">
              Prêt à inscrire votre enfant ?
            </h2>
            <p className="mt-4 text-white/80 leading-relaxed">
              Le formulaire prend 5-10 minutes. Sous réserve de places disponibles, notre équipe vous recontactera après validation pour fixer le rendez-vous de paiement.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <EnrollmentCTA size="lg" variant="white-on-blue" testId="cta-request-bottom" />
              <ReenrollmentCTA size="lg" variant="white-on-blue" testId="cta-reenrollment-bottom" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function FeeTile({ label, amount }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 bg-white rounded-full px-3 py-1 border border-[#1E3A8A]/15">
      <span className="text-[10px] uppercase tracking-[0.15em] text-[#5c6374]">{label}</span>
      <span className="font-[Outfit] font-semibold text-base text-[#1E3A8A]">{Math.round(Number(amount))} €</span>
    </span>
  );
}
